/**
 * Organizations Custom Hooks
 *
 * Business logic hooks for organization management features.
 */

import { useCallback, useMemo, useState } from 'react';
import { useUser } from '@features/user';
import { hasBillingAccess } from '@utils/roleUtils';
import type {
  ProviderInvitationResponse,
  ProviderOrganizationMemberResponse,
} from '@store/generated/organizations';

import type {
  LoadingOperations,
  OrganizationInvitation,
  OrganizationMemberTableRow,
} from '../types';
import { useGetOrganizationQuery } from './api';

/**
 * Transform API member data to table row format
 */
export function useTransformMembers(
  members?: ProviderOrganizationMemberResponse[]
): {
  activeMembers: OrganizationMemberTableRow[];
  inactiveMembers: OrganizationMemberTableRow[];
} {
  return useMemo(() => {
    if (!members) {
      return { activeMembers: [], inactiveMembers: [] };
    }

    const transformed = members.map(
      (member): OrganizationMemberTableRow => ({
        userId: member.user_id,
        name: member.name || 'Unknown',
        email: member.email || '',
        roles: member.roles || [],
        status: member.status === 'inactive' ? 'inactive' : 'active',
        deactivatedAt: member.deactivated_at,
        notesCreated: 0, // This would need to come from additional user metrics
        phoneNumber: member.phone_number,
        profilePicture: undefined, // Not available in provider response
      })
    );

    const activeMembers = transformed.filter(m => m.status === 'active');
    const inactiveMembers = transformed.filter(m => m.status === 'inactive');

    return { activeMembers, inactiveMembers };
  }, [members]);
}

/**
 * Transform API invitation data
 */
export function useTransformInvitations(
  invitations?: ProviderInvitationResponse[]
): OrganizationInvitation[] {
  return useMemo(() => {
    if (!invitations) return [];

    return invitations.map((invitation): OrganizationInvitation => ({
      invitationId: invitation.invitation_id,
      email: invitation.email,
      role: invitation.role,
      sentAt: invitation.sent_at,
      expiresAt: invitation.expires_at,
      status: invitation.status,
    }));
  }, [invitations]);
}

/**
 * Hook for managing loading states for individual operations
 */
export function useOperationLoading() {
  const [loadingOperations, setLoadingOperations] = useState<LoadingOperations>({});

  const setOperationLoading = useCallback((key: string, loading: boolean) => {
    setLoadingOperations(prev => ({
      ...prev,
      [key]: loading,
    }));
  }, []);

  const isOperationLoading = useCallback(
    (key: string) => loadingOperations[key] || false,
    [loadingOperations]
  );

  return {
    loadingOperations,
    setOperationLoading,
    isOperationLoading,
  };
}

/**
 * Check if an invitation is expired
 */
export function useInvitationStatus(expiresAt: string): 'pending' | 'expired' {
  return useMemo(() => {
    const expiryDate = new Date(expiresAt);
    return Date.now() > expiryDate.getTime() ? 'expired' : 'pending';
  }, [expiresAt]);
}

/**
 * Hook for organization access and permissions
 *
 * Provides common organization data and permission checks used across
 * organization management pages.
 */
export function useOrganizationAccess() {
  const { userState } = useUser();

  const userIsBillingAdmin = hasBillingAccess(userState?.group_user_roles);
  const userIsInGroup = (userState?.group_ids ?? []).length > 0;

  const {
    data: organizationData,
    isLoading: orgLoading,
    refetch: refetchOrg,
  } = useGetOrganizationQuery(undefined, {
    skip: !userIsInGroup,
  });

  return {
    userState,
    userIsBillingAdmin,
    userIsInGroup,
    organizationData,
    orgLoading,
    refetchOrg,
  };
}